// Vocabulary shared by the Finance page and the per-customer Finance tab.
// One copy, because the two surfaces have to name the same thing the same way —
// a resource labelled "Sitelist tenancy" on one page and "tenancy" on the other
// reads as two different facts.

import type { PillTone } from "../../mui";

export const SOURCE_LABELS: Record<string, string> = {
  unresolved: "Unresolved",
  default: "Binned to MOD",
  tag: "AWS tag",
  alias: "Name match",
  rule: "Rule",
  parent: "Parent resource",
  tenancy: "Sitelist tenancy",
  override: "Manual override",
};

export const SOURCE_HINTS: Record<string, string> = {
  unresolved: "nothing could attribute it",
  default: "no owner found — deleted resources, orphans and idle addresses. Cleanup worklist, not a mapping one.",
  tag: "the Account tag on the resource",
  alias: "customer alias found in the resource name",
  rule: "matched a pattern rule",
  parent: "inherited from its attached instance / source volume",
  tenancy: "the live sitelist knows who occupies this host",
  override: "assigned by hand — always wins, always retroactive",
};

export const SOURCE_ORDER = ["unresolved", "default", "tag", "alias", "rule", "parent", "tenancy", "override"];

export function sourceTone(source: string): PillTone {
  if (source === "unresolved") return "danger";
  if (source === "default" || source === "tag" || source === "alias") return "warning";
  if (source === "override" || source === "tenancy") return "success";
  return "neutral";
}

/** Strip the ARN preamble — the tail is the only part anyone reads. */
export function shortResourceId(id: string): string {
  if (!id) return "(no resource id)";
  const m = /^arn:aws:[^:]+:[^:]*:[^:]*:(.+)$/.exec(id);
  return m ? m[1] : id;
}

/** CUR service codes are billing identifiers, not names people use. */
const SERVICE_LABELS: Record<string, string> = {
  AmazonEC2: "EC2",
  AmazonS3: "S3",
  AmazonRDS: "RDS",
  AmazonCloudWatch: "CloudWatch",
  AWSLambda: "Lambda",
  AmazonEFS: "EFS",
  AmazonRoute53: "Route 53",
  AmazonCloudFront: "CloudFront",
  AWSELB: "Load balancing",
  AWSDataTransfer: "Data transfer",
  AmazonSES: "SES",
  AmazonSNS: "SNS",
  AmazonSQS: "SQS",
  AmazonDynamoDB: "DynamoDB",
  AmazonVPC: "VPC",
  awskms: "KMS",
  AWSSecretsManager: "Secrets Manager",
  AWSConfig: "Config",
  AWSCloudTrail: "CloudTrail",
  AWSBackup: "Backup",
  AmazonECR: "ECR",
  AWSCodePipeline: "CodePipeline",
  AmazonApiGateway: "API Gateway",
  AmazonCognito: "Cognito",
  AmazonAthena: "Athena",
};

/** What the money actually buys, in words an account manager can use. */
const SERVICE_PURPOSE: Record<string, string> = {
  AmazonEC2: "servers, disks and public IPs",
  AmazonS3: "backups and file storage",
  AmazonRDS: "managed databases",
  AmazonCloudWatch: "logs, metrics and alarms",
  AWSLambda: "on-demand functions",
  AmazonEFS: "shared file storage",
  AmazonRoute53: "DNS",
  AmazonCloudFront: "content delivery",
  AWSELB: "load balancers",
  AWSDataTransfer: "network traffic",
  AmazonSES: "outbound email",
  AmazonSNS: "notifications",
  AmazonSQS: "queues",
  AmazonDynamoDB: "key-value tables",
  AmazonVPC: "private networking",
  awskms: "encryption keys",
  AWSSecretsManager: "stored credentials",
  AWSBackup: "backup jobs",
  AmazonApiGateway: "API endpoints",
  AmazonCognito: "sign-in",
};

export const serviceLabel = (code: string): string => SERVICE_LABELS[code] ?? code;
export const servicePurpose = (code: string): string => SERVICE_PURPOSE[code] ?? "";

/** ap-southeast-2 → Sydney. Regions are where the cost sits, and the pro-rata
 *  driver is region-scoped, so the place matters on a shared line. */
const REGION_LABELS: Record<string, string> = {
  "ap-southeast-2": "Sydney",
  "ap-southeast-1": "Singapore",
  "ap-southeast-4": "Melbourne",
  "us-east-1": "N. Virginia",
  "us-east-2": "Ohio",
  "us-west-1": "N. California",
  "us-west-2": "Oregon",
  "ca-central-1": "Canada",
  "af-south-1": "Cape Town",
  "eu-west-1": "Ireland",
  "eu-west-2": "London",
  "eu-central-1": "Frankfurt",
  global: "Global",
  "": "Unregioned",
};

export const regionLabel = (region: string): string => REGION_LABELS[region] ?? region;

/** Percentages here run from "half the bill" to "a rounding error", so a fixed
 *  precision either wastes space or collapses small shares to 0%. */
export function fmtShare(share: number): string {
  const pct = share * 100;
  if (!isFinite(pct) || pct <= 0) return "0%";
  if (pct >= 10) return `${Math.round(pct)}%`;
  if (pct >= 1) return `${pct.toFixed(1)}%`;
  return `${pct.toFixed(2)}%`;
}

/** Cents matter on a $0.40 line and are noise on a $400 one. */
export function fmtUSDPrecise(n: number): string {
  if (!isFinite(n)) return "—";
  if (Math.abs(n) >= 100) return `$${Math.round(n).toLocaleString("en-US")}`;
  return `$${n.toFixed(2)}`;
}

/** "26 Jun" — the window bounds are read as a span, so the year is noise. */
export function fmtDayShort(day: string): string {
  if (!day) return "—";
  const d = new Date(`${day}T00:00:00Z`);
  if (isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
}
